"use client";

import { ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Transparency() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tastingNotes = [{
    label: "Radical Honesty"
  }, {
    label: "Direct Impact"
  }, {
    label: "Community Trust"
  }];
  const stats = [{
    label: "PROCEEDS",
    value: "15%"
  }, {
    label: "CAUSE",
    value: "Active"
  }, {
    label: "STATUS",
    value: "Verified"
  }];
  return <section data-section-id="2930" id="your-transparency-promise" className="bg-slate-50 text-slate-900 relative w-full overflow-hidden py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-16 lg:gap-20 items-center">
          {}
          <div ref={imageRef} className={`relative md:-my-16 transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative group">
              <div className="absolute inset-0 bg-blue-600/10 blur-3xl rounded-lg" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-lg bg-slate-200/40 shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/impact_transparency_report.webp" alt="Community members collaborating on a humanitarian awareness project" loading="lazy" decoding="async" className="w-full h-auto object-cover transition-transform duration-700 ease-out group-hover:-translate-y-2" />
              </div>
            </div>
          </div>

          {}
          <div ref={contentRef} className={`flex flex-col transition-all duration-1000 ease-out delay-150 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="mb-6">
              <span className="font-default text-xs uppercase tracking-widest text-blue-700">
                Mission Report — Updated Q4 2024
              </span>
            </div>

            {}
            <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-[80px] leading-[0.95] tracking-tight mb-8 text-slate-900">
              Our Promise
            </h2>

            {}
            <div className="flex flex-wrap gap-2.5 mb-8">
              {tastingNotes.map((note, i) => <Badge key={note.label} data-index={i} variant="outline" className="rounded-lg border-blue-200 bg-blue-50 text-blue-900 px-4 py-1.5 text-xs font-normal font-default tracking-wide">
                  {note.label}
                </Badge>)}
            </div>

            {}
            <p className="font-default text-lg md:text-xl leading-relaxed text-slate-700 mb-10 max-w-xl">
              We believe in total clarity. Every limited merch drop is tied directly to a humanitarian cause, ensuring that your support translates into measurable change. We track every dollar to ensure our impact remains forever fresh and accountable.
            </p>

            {}
            <div className="flex items-center gap-3 md:gap-4 mb-10 flex-wrap">
              {stats.map((stat, i) => <div key={stat.label} data-index={i} className="flex items-center gap-3 md:gap-4">
                  {i > 0 && <span className="text-slate-400/60" aria-hidden="true">·</span>}
                  <div className="flex items-baseline gap-2">
                    <span className="font-default text-xs uppercase tracking-widest text-slate-500">
                      {stat.label}
                    </span>
                    <span className="font-default text-base font-bold text-slate-900">
                      {stat.value}
                    </span>
                  </div>
                </div>)}
            </div>

            {}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6">
              <Button asChild size="lg" className="rounded-lg bg-blue-600 text-white hover:bg-blue-700 px-8 py-6 h-auto text-base font-default font-medium shadow-md">
                <Link to="/AboutImpactPage">View Impact Report</Link>
              </Button>
              <Button asChild variant="link" className="text-blue-700 hover:text-blue-900 px-0 h-auto font-default text-base group">
                <Link to="/CurrentCausesPage" className="inline-flex items-center gap-2">
                  Explore Current Causes
                  <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}